/**
 * Breach Cost Calculator Constants
 * Based on IBM Cost of a Data Breach Report 2024
 * Industry benchmarks and multipliers
 */

import { IndustrySector, GeographicRegion, SecurityMaturityLevel } from '@/types/calculator';

// ============================================================================
// MATURITY LEVEL NAMES
// ============================================================================

// Consistent naming across all calculators (aligned with maturity assessment)
export const SECURITY_MATURITY_NAMES: Record<SecurityMaturityLevel, string> = {
  minimal: 'Minimal',
  basic: 'Basic',
  moderate: 'Moderate',
  advanced: 'Advanced',
  optimized: 'Optimized'
};

// ============================================================================
// IBM 2024 GLOBAL AVERAGES
// ============================================================================

export const IBM_GLOBAL_AVERAGES = {
  totalCost: 4880000,          // $4.88M
  costPerRecord: 165,          // $165
  mttd: 194,                   // Mean Time to Detect (days)
  mttc: 64,                    // Mean Time to Contain (days)
  totalLifecycle: 258          // Total breach lifecycle (days)
};

// ============================================================================
// INDUSTRY MULTIPLIERS
// ============================================================================

// Multiplier relative to global average ($4.88M)
export const INDUSTRY_BREACH_COSTS: Record<IndustrySector, number> = {
  healthcare: 10930000,        // $10.93M (2.24x)
  pharmaceutical: 9770000,     // $9.77M (2.00x)
  financial: 6080000,          // $6.08M (1.25x)
  technology: 5520000,         // $5.52M (1.13x)
  energy: 5400000,             // $5.40M (1.11x)
  industrial: 4780000,         // $4.78M (0.98x)
  manufacturing: 4780000,      // $4.78M (0.98x)
  retail: 3480000,             // $3.48M (0.71x)
  hospitality: 3300000,        // $3.30M (0.68x)
  media: 3200000,              // $3.20M (0.66x)
  education: 3500000,          // $3.50M (0.72x)
  professional: 4000000,       // $4.00M (0.82x)
  government: 2300000,         // $2.30M (0.47x)
  other: 4880000               // $4.88M (1.00x)
};

export const INDUSTRY_MULTIPLIERS: Record<IndustrySector, number> = {
  healthcare: 2.24,
  pharmaceutical: 2.00,
  financial: 1.25,
  technology: 1.13,
  energy: 1.11,
  industrial: 0.98,
  manufacturing: 0.98,
  retail: 0.71,
  hospitality: 0.68,
  media: 0.66,
  education: 0.72,
  professional: 0.82,
  government: 0.47,
  other: 1.00
};

// ============================================================================
// INDUSTRY-SPECIFIC BREACH LIFECYCLE
// ============================================================================

// Industry-specific Mean Time to Detect (MTTD) and Mean Time to Contain (MTTC)
// Source: IBM Cost of a Data Breach Report 2024
// Methodology: Industry-specific variations based on IBM's analysis of 604 organizations
// across 17 countries and regions. Healthcare and pharmaceutical show longer detection
// times due to complex IT environments and legacy systems. Financial services and
// technology sectors show faster detection due to regulatory requirements and
// security-first cultures. Global average: 194 days MTTD + 64 days MTTC = 258 days total.
export const INDUSTRY_BREACH_LIFECYCLE: Record<
  IndustrySector,
  { mttd: number; mttc: number; total: number }
> = {
  healthcare: { mttd: 236, mttc: 73, total: 309 },        // IBM 2024: Complex IT, legacy systems, +20% vs global
  pharmaceutical: { mttd: 225, mttc: 70, total: 295 },    // IBM 2024: Similar to healthcare, +14% vs global
  financial: { mttd: 166, mttc: 56, total: 222 },         // IBM 2024: Better monitoring, regulations, -14% vs global
  technology: { mttd: 157, mttc: 54, total: 211 },        // IBM 2024: Security-first culture, -18% vs global
  energy: { mttd: 213, mttc: 69, total: 282 },            // IBM 2024: OT/IT complexity, +9% vs global
  industrial: { mttd: 218, mttc: 71, total: 289 },        // IBM 2024: OT/IT convergence, +12% vs global
  manufacturing: { mttd: 218, mttc: 71, total: 289 },     // IBM 2024: Similar to industrial, +12% vs global
  retail: { mttd: 197, mttc: 66, total: 263 },            // IBM 2024: Seasonal variations, +2% vs global
  hospitality: { mttd: 212, mttc: 68, total: 280 },       // IBM 2024: Often underfunded security, +9% vs global
  media: { mttd: 201, mttc: 65, total: 266 },             // IBM 2024: Variable security maturity, +3% vs global
  education: { mttd: 227, mttc: 75, total: 302 },         // IBM 2024: Limited security resources, +17% vs global
  professional: { mttd: 189, mttc: 62, total: 251 },      // IBM 2024: Moderate security investment, -3% vs global
  government: { mttd: 183, mttc: 78, total: 261 },        // IBM 2024: Good monitoring, slow bureaucracy, +1% vs global
  other: { mttd: 194, mttc: 64, total: 258 }              // IBM 2024: Global average baseline
};

// ============================================================================
// REGION MULTIPLIERS
// ============================================================================

export const REGION_BREACH_COSTS: Record<GeographicRegion, number> = {
  united_states: 9360000,      // $9.36M
  middle_east: 8750000,        // $8.75M
  canada: 5730000,             // $5.73M
  germany: 5140000,            // $5.14M
  uk: 4910000,                 // $4.91M
  france: 4680000,             // $4.68M
  asia_pacific: 3460000,       // $3.46M
  latin_america: 3170000,      // $3.17M
  other: 4150000               // $4.15M (estimated average)
};

export const REGION_MULTIPLIERS: Record<GeographicRegion, number> = {
  united_states: 1.92,
  middle_east: 1.79,
  canada: 1.17,
  germany: 1.05,
  uk: 1.01,
  france: 0.96,
  asia_pacific: 0.71,
  latin_america: 0.65,
  other: 0.85
};

// ============================================================================
// RECORD COUNT SCALE FACTORS
// ============================================================================

// Economies of scale - larger breaches have lower per-record costs
export function getScaleFactor(recordCount: number): number {
  if (recordCount < 10000) return 1.3;        // Higher per-record cost
  if (recordCount < 100000) return 1.1;
  if (recordCount < 1000000) return 1.0;      // Baseline
  if (recordCount < 10000000) return 0.85;    // Lower per-record cost
  if (recordCount < 50000000) return 0.70;
  return 0.55;                                // Mega breach economies
}

// ============================================================================
// MATURITY MULTIPLIERS
// ============================================================================

export const MATURITY_MULTIPLIERS = {
  minimal: 1.50,      // +50% cost (slow detection, poor response)
  basic: 1.25,        // +25% cost
  moderate: 1.00,     // Baseline
  advanced: 0.80,     // -20% cost (fast detection, automated response)
  optimized: 0.65     // -35% cost (proactive threat hunting, AI-driven automation)
};

// ============================================================================
// COST COMPONENTS
// ============================================================================

export const DETECTION_COSTS = {
  forensic_investigation: 180000,    // $180K
  security_analytics: 95000,         // $95K
  incident_response_team: 250000,    // $250K
  breach_assessment: 75000,          // $75K
  evidence_collection: 60000         // $60K
};

export const NOTIFICATION_COSTS = {
  customer_notification_per_record: 2.50,  // $2.50 per customer
  credit_monitoring_per_record: 15,        // $15 per person for 1-2 years
  regulatory_notification: 45000,          // $45K
  media_management: 120000,                // $120K
  call_center_setup: 200000,               // $200K
  website_updates: 25000                   // $25K
};

export const POST_BREACH_COSTS = {
  security_upgrades: 450000,         // $450K
  consulting_services: 280000,       // $280K
  employee_training: 85000,          // $85K
  insurance_premium_increase: 125000, // $125K (3-year avg)
  brand_recovery: 350000,            // $350K
  executive_time: 180000             // $180K (opportunity cost)
};

// ============================================================================
// MITIGATION FACTORS (Cost Savers)
// ============================================================================

export const MITIGATION_FACTORS = {
  tested_ir_plan: 0.75,        // -25% (saves $1.49M per IBM)
  ai_extensively: 0.82,        // -18% (saves $1.76M per IBM)
  ai_moderately: 0.93,         // -7%
  ai_minimally: 0.98,          // -2%
  lifecycle_under_200: 0.76,   // -24% (saves $1.2M per IBM)
  lifecycle_over_300: 1.23,    // +23% (adds $1.1M per IBM)
  hybrid_cloud: 1.15,          // +15%
  remote_over_50: 1.10,        // +10%
  repeat_breach: 1.40          // +40%
};

// ============================================================================
// REGULATORY FINES
// ============================================================================

export function calculateGDPRFine(revenue: number): number {
  // Up to 4% of annual revenue or €20M, whichever is higher
  const fourPercent = revenue * 0.04;
  const twentyMillion = 20000000;
  return Math.max(fourPercent, twentyMillion);
}

export function calculateHIPAAFine(recordCount: number): number {
  // Tier 4 violation: $50K per record, max $1.5M per year
  const recordFine = recordCount * 50000;
  return Math.min(recordFine, 1500000);
}

export function calculatePCIDSSFine(recordCount: number): number {
  // Card brand fines + acquirer fines
  const cardFines = Math.min(recordCount * 70, 5000000);
  const baseFine = 500000;
  return cardFines + baseFine;
}

export function calculateStateFine(): number {
  // Average state privacy law fine (CCPA, etc.)
  return 275000;
}

export function calculateFinancialFine(): number {
  // GLBA, SOX, etc.
  return 500000;
}

// ============================================================================
// BUSINESS DISRUPTION PERCENTAGES
// ============================================================================

export const DISRUPTION_PERCENTAGES: Record<string, number> = {
  retail: 0.35,           // 35% revenue loss during breach
  hospitality: 0.35,      // 35%
  healthcare: 0.25,       // 25%
  financial: 0.25,        // 25%
  technology: 0.20,       // 20%
  manufacturing: 0.20,    // 20%
  other: 0.15             // 15%
};

export function getDisruptionPercentage(industry: string): number {
  return DISRUPTION_PERCENTAGES[industry] || DISRUPTION_PERCENTAGES.other;
}

// ============================================================================
// EFFECTIVENESS DATA (For Recommendations)
// ============================================================================

export const PREVENTION_EFFECTIVENESS = {
  mdr_soc: {
    riskReduction: 45,
    annualCost: 150000,
    description: '24/7 monitoring reduces detection time from 194 days to <24 hours'
  },
  tested_ir_plan: {
    riskReduction: 25,
    annualCost: 35000,
    description: 'Tested incident response plan saves $1.49M on average'
  },
  ai_security: {
    riskReduction: 18,
    annualCost: 95000,
    description: 'AI-driven security analytics saves $1.76M through automation'
  },
  edr: {
    riskReduction: 25,
    annualCost: 96000,
    description: 'Endpoint detection and response reduces breach likelihood by 25%'
  },
  mfa: {
    riskReduction: 40, // 99.2% of account compromises (40% of breaches)
    annualCost: 18000,
    description: 'MFA prevents 99.2% of account compromise attacks'
  },
  backup_dr: {
    riskReduction: 24, // 95% ransomware reduction (25% of breaches)
    annualCost: 120000,
    description: 'Backup and DR provides 95% ransomware recovery success'
  }
};
